import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { schemaToParameters } from '../../../../utils/schemaToParameters';

export const automation_create: SelfContainedCommand = {
  id: 'automation_create',
  description: `Create an Automation — an AI worker that runs on a schedule, reacts to workspace events, or runs on demand.

**TRIGGER TYPES:**
- manual: only runs when explicitly invoked
- schedule: runs on a cron schedule (provide cron expression)
- world_event: fires when a named event occurs (entity.opened, entity.created, task.completed, email.received, message.sent, etc.)

Use \`triggers\` for full control (supports multiple triggers, event filters). Use \`triggerType\` for simple single-trigger automations.

**CRON SCHEDULE TIMEZONE RULE — CRITICAL:**
ALL cron expressions use UTC hours and minutes. Always set timezone to "UTC" (or omit it — it defaults to UTC). Never use the user's local timezone. If the user says "9am", ask what timezone they mean and convert to UTC before writing the cron expression.

**CRON EXPRESSION EXAMPLES (all times are UTC):**
- "0 9 * * 1-5" = weekdays at 9:00 UTC
- "0 9 * * 1"   = every Monday at 9:00 UTC
- "0 8 * * *"   = every day at 8:00 UTC

**EXAMPLES:**

Create a reactive automation that fires on entity.opened:
{ "name": "His Girl Friday", "systemPrompt": "When activated, post ONE sharp witty quip in His Girl Friday style.", "triggers": [{ "type": "world_event", "eventType": "entity.opened" }] }

Create a weekly scheduled automation (9am UTC):
{ "name": "Weekly Sales Triage", "systemPrompt": "Review all leads from this week. For each warm lead, draft a follow-up email.", "triggerType": "schedule", "cron": "0 9 * * 1", "humanFrequency": "Every Monday at 9:00 UTC" }

Create a manual automation:
{ "name": "Generate Q1 Report", "systemPrompt": "Pull together all task completions, notes, and emails from Q1 and generate a comprehensive report.", "triggerType": "manual" }

Create an email triage automation:
{ "name": "Inbox Triage", "systemPrompt": "When an email arrives, classify it by urgency and create a task if action is needed.", "triggers": [{ "type": "world_event", "eventType": "email.received" }] }`,
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Automation name (e.g. "Weekly Report", "Inbox Triage", "His Girl Friday")',
        },
        systemPrompt: {
          type: 'string',
          description: 'Standing instructions for the automation. Define what it should do when it runs.',
        },
        description: {
          type: 'string',
          description: 'Short description of what this automation does and when it activates.',
        },
        triggers: {
          type: 'array',
          items: { type: 'object' },
          description: "Trigger definitions for full control. Each entry: { type: 'world_event', eventType: string, filter?: Record<string,string> } | { type: 'cron', cron: string, humanFrequency?: string, timezone: 'UTC' } | { type: 'manual' }. Cron hours/minutes are always UTC. Takes precedence over triggerType.",
        },
        triggerType: {
          type: 'string',
          enum: ['schedule', 'manual', 'world_event'],
          description: "Simple trigger type: 'schedule' (provide cron), 'manual', or 'world_event'. Ignored when triggers is provided.",
        },
        cron: {
          type: 'string',
          description: 'Cron expression (required when triggerType is "schedule"). All times are UTC. Example: "0 17 * * 1" = Every Monday at 17:00 UTC (= 9am Pacific).',
        },
        humanFrequency: {
          type: 'string',
          description: 'Human-readable frequency label. Always include UTC, e.g. "Every Monday at 17:00 UTC".',
        },
        timezone: {
          type: 'string',
          description: 'Always "UTC". Cron expressions must use UTC hours and minutes. Do not pass the user\'s local timezone here.',
        },
        model: {
          type: 'string',
          description: 'LLM model to use. Omit to use the platform default.',
        },
        maxDurationSeconds: {
          type: 'number',
          description: 'Run timeout in seconds (default: 300). Increase for long-running automations.',
          default: 300,
        },
        enabled: {
          type: 'boolean',
          description: 'Whether the automation is active on creation (default: true).',
        },
        folderId: {
          type: 'string',
          description: 'Folder ID (group_* llmId or 24-char ObjectId) to place this automation in. When provided, the automation appears as a child of that folder in the workspace tree instead of at the current space root.',
        },
      },
      required: ['name', 'systemPrompt'],
    }),
  },

  examples: {
    good: [
      {
        input: {
          name: 'His Girl Friday',
          systemPrompt: 'When activated, post ONE sharp witty quip in His Girl Friday style.',
          triggers: [{ type: 'world_event', eventType: 'entity.opened' }],
          allowedTools: ['task_complete'],
        },
        expectedOutput: createSuccessResult('Automation created', { automationId: 'automation_123' }),
        description: 'Create a reactive automation triggered on entity.opened',
      },
      {
        input: {
          name: 'Weekly Report',
          systemPrompt: 'Summarize all tasks completed this week into a note.',
          triggerType: 'schedule',
          cron: '0 9 * * 5',
          humanFrequency: 'Every Friday at 9:00 UTC',
        },
        expectedOutput: createSuccessResult('Automation created', { automationId: 'automation_456' }),
        description: 'Create a scheduled automation',
      },
    ],
    bad: [
      {
        input: { name: 'Test', systemPrompt: '', triggerType: 'manual' },
        error: 'systemPrompt is required',
        why: 'The systemPrompt describes what the automation should do',
        fix: 'Provide a clear systemPrompt describing the automation task',
      },
    ],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      name = 'Untitled Automation',
      systemPrompt = '',
      description = '',
      triggers,
      triggerType,
      cron,
      humanFrequency,
      timezone,
      model,
      maxDurationSeconds,
      enabled,
      spaceId: providedSpaceId,
      folderId,
    } = params;

    if (!name?.trim()) return createErrorResult('name is required', '', 'validation');
    if (!systemPrompt?.trim()) return createErrorResult('systemPrompt is required', '', 'validation');

    // Resolve folderId → ObjectId so the dataspace_edge parent_id is a real ObjectId.
    let resolvedFolderId: string | undefined;
    if (folderId) {
      const isObjectId = /^[0-9a-f]{24}$/i.test(folderId);
      if (isObjectId) {
        resolvedFolderId = folderId;
      } else {
        try {
          const { API_BASE_URL } = await import('../../../../api/config');
          const authToken = localStorage.getItem('authToken') || '';
          const res = await fetch(`${API_BASE_URL}/entities?llmId=${encodeURIComponent(folderId)}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (res.ok) {
            const data = await res.json();
            const folders = Array.isArray(data) ? data : (data.entities ?? []);
            if (folders[0]?._id) resolvedFolderId = String(folders[0]._id);
          }
        } catch {
          // Fall through — use space root
        }
        if (!resolvedFolderId) {
          console.warn(`[automation_create] folderId "${folderId}" not found — falling back to space root`);
        }
      }
    }

    const spaceId = resolvedFolderId || providedSpaceId || context.spaceId;

    const TRIGGER_TYPE_MAP: Record<string, string> = { schedule: 'cron', event: 'world_event' };

    // Build triggers array — prefer explicit triggers[] over triggerType shorthand
    let normalizedTriggers: any[];
    if (Array.isArray(triggers) && triggers.length > 0) {
      normalizedTriggers = triggers.map((t: any) => {
        if (t.type === 'cron') return { ...t, timezone: 'UTC' };
        if (t.type === 'world_event' || t.type === 'manual') return t;
        return { type: 'world_event' as const, eventType: t.type, filter: t.filter, spaceId: t.spaceId };
      });
    } else if (triggerType) {
      const backendType = TRIGGER_TYPE_MAP[triggerType] ?? triggerType;
      if (backendType === 'cron' && !cron) return createErrorResult('cron is required when triggerType is "schedule"', '', 'validation');
      normalizedTriggers = [{
        type: backendType,
        cron: cron || undefined,
        humanFrequency: humanFrequency || cron || undefined,
        timezone: timezone || 'UTC',
      }];
    } else {
      normalizedTriggers = [{ type: 'manual' }];
    }

    const worldEventTriggers = normalizedTriggers.filter(t => t.type === 'world_event');

    console.log(
      `[automation_create] Creating Automation: "${name}" | triggers=${normalizedTriggers.map(t => t.type === 'world_event' ? (t as any).eventType : t.type).join(',')} | thread=${context.threadId?.slice(-8)}`,
    );

    try {
      const { API_BASE_URL } = await import('../../../../api/config');

      const body: Record<string, any> = {
        name: name.trim(),
        systemPrompt: systemPrompt.trim(),
        description,
        enabled: enabled !== undefined ? enabled : true,
        triggers: normalizedTriggers,
        modelPreference: model || undefined,
        maxDurationSeconds: maxDurationSeconds ?? 300,
        handoffPolicy: 'summarize',
        visibility: 'user_visible',
        spaceId,
        defaultThreadId: context.threadId || '',
      };

      const authToken = localStorage.getItem('authToken') || '';
      const res = await fetch(`${API_BASE_URL}/automations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`POST /api/automations ${res.status}: ${errText}`);
      }

      const newEntity = await res.json();
      const automationId = newEntity._id || newEntity.id;

      console.log(`[automation_create] Created Automation: "${name}" id=${automationId}`);

      if (worldEventTriggers.length > 0) {
        notifySupervisorRefresh(spaceId, context.threadId).catch(() => {});
      }

      return createSuccessResult(`Created automation: ${name}`, {
        automationId,
        automation: {
          id: automationId,
          name: newEntity.name,
          triggers: normalizedTriggers,
        },
      });
    } catch (error) {
      console.error(`[automation_create] Failed to create Automation "${name}":`, error);
      return createErrorResult(
        'Failed to create automation',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
      );
    }
  },
};

async function notifySupervisorRefresh(spaceId: string, threadId?: string): Promise<void> {
  try {
    const [{ getCurrentDataspaceId }, { API_BASE_URL }] = await Promise.all([
      import('../../../../services/worldEventsService'),
      import('../../../../api/config'),
    ]);
    const dataspaceId = getCurrentDataspaceId();
    if (!dataspaceId || !spaceId) return;

    const authToken = localStorage.getItem('authToken') || '';
    console.log(`[automation_create] Notifying supervisor refresh for space=...${spaceId.slice(-6)}`);

    const res = await fetch(`${API_BASE_URL}/agent/supervisor/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ dataspaceId, spaceId, threadId }),
    });

    if (res.ok) {
      const data = await res.json();
      console.log(`[automation_create] Supervisor refreshed: ${data.triggers} trigger(s) loaded`);
    } else {
      console.warn(`[automation_create] Supervisor refresh returned ${res.status} (non-fatal)`);
    }
  } catch (err: any) {
    console.warn(`[automation_create] Supervisor refresh failed (non-fatal):`, err.message);
  }
}

entityCreationRegistry.register({
  commandId: 'automation_create',
  entityType: 'Automation',
  displayName: 'Automation',
  icon: { type: 'phosphor', phosphorIcon: 'Lightning', phosphorWeight: 'duotone' },
  description: 'AI worker for background tasks, scheduled jobs, and event-driven automations',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 110,
});
